import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Search, ChevronDown, User, ArrowRight } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS, SUBCATEGORIES, SUBCATEGORY_LABELS } from "../lib/constants";
import { useBrand } from "../context/BrandContext";
import { useAuth } from "../context/AuthContext";

// App-icon tile: a three-bar mini chart in white on a solid indigo square.
// No colour ramp — the mark reads as a product icon, not an illustration.
function AppMark() {
    return (
        <span className="grid place-items-center w-9 h-9 rounded-lg bg-accent shrink-0">
            <span className="flex items-end gap-[3px]">
                <span className="w-[3px] h-2 rounded-sm bg-accent-foreground/70" />
                <span className="w-[3px] h-3.5 rounded-sm bg-accent-foreground" />
                <span className="w-[3px] h-[7px] rounded-sm bg-accent-foreground/70" />
            </span>
        </span>
    );
}

// Single-row product header: identity left, ghost-pill navigation centred,
// a bordered search affordance and one solid indigo CTA on the right.
export function Navbar() {
    const brand = useBrand();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [q, setQ] = useState("");

    const doSearch = (e) => {
        e.preventDefault();
        if (q.trim()) {
            navigate(`/search?q=${encodeURIComponent(q.trim())}`);
            setSearchOpen(false);
            setMobileOpen(false);
            setQ("");
        }
    };

    const navPill =
        "flex items-center gap-1 px-3 py-2 rounded-lg text-[13px] font-medium text-dim hover:text-accent hover:bg-accent-soft transition-colors duration-150";

    return (
        <header className="sticky top-0 z-50 glass border-b border-line" data-testid="navbar">
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center h-16 gap-4">
                    <button
                        className="xl:hidden -ml-1 grid place-items-center w-9 h-9 rounded-lg border border-line bg-surface text-dim hover:text-foreground hover:bg-surface-raised transition-colors duration-150"
                        onClick={() => setMobileOpen((v) => !v)}
                        aria-label="Toggle menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {mobileOpen ? <X size={17} /> : <Menu size={17} />}
                    </button>

                    <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="navbar-logo">
                        <AppMark />
                        <span className="font-display text-[17px] sm:text-lg font-extrabold tracking-[-0.02em] text-foreground">
                            {brand.site_name}
                        </span>
                    </Link>

                    {/* Primary nav — ghost pills, centred */}
                    <nav className="hidden xl:flex items-center gap-0.5 flex-1 justify-center">
                        {CATEGORIES.map((cat) => (
                            <div key={cat} className="relative group">
                                <Link to={`/category/${cat}`} data-testid={`nav-cat-${cat}`} className={navPill}>
                                    {CATEGORY_LABELS[cat]}
                                    {cat === "personal-finance" && (
                                        <ChevronDown
                                            size={13}
                                            className="text-line-strong transition-transform duration-200 group-hover:rotate-180"
                                        />
                                    )}
                                </Link>
                                {cat === "personal-finance" && (
                                    <div className="absolute left-0 top-full pt-2 w-60 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-200 z-50">
                                        <div className="rounded-xl border border-line bg-popover p-1.5 shadow-lift">
                                            {SUBCATEGORIES.map((sub) => (
                                                <Link
                                                    key={sub}
                                                    to={`/category/personal-finance/${sub}`}
                                                    data-testid={`nav-sub-${sub}`}
                                                    className="group/sub flex items-center justify-between px-3 py-2 rounded-lg text-[13px] font-medium text-dim hover:text-accent hover:bg-accent-soft transition-colors duration-150"
                                                >
                                                    {SUBCATEGORY_LABELS[sub]}
                                                    <ArrowRight size={13} className="text-accent opacity-0 -translate-x-1 transition-[opacity,transform] duration-150 group-hover/sub:opacity-100 group-hover/sub:translate-x-0" />
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        <Link to="/archive" data-testid="nav-archive" className={navPill}>
                            Archive
                        </Link>
                    </nav>

                    <div className="flex items-center gap-2 ml-auto xl:ml-0">
                        <button
                            onClick={() => setSearchOpen((v) => !v)}
                            className="grid place-items-center w-9 h-9 rounded-lg border border-line bg-surface shadow-card text-dim hover:text-accent hover:bg-surface-raised transition-colors duration-150"
                            aria-label="Search"
                            data-testid="navbar-search-toggle"
                        >
                            <Search size={16} />
                        </button>
                        {user ? (
                            <div className="hidden sm:flex items-center gap-2">
                                <Link
                                    to="/account"
                                    data-testid="navbar-account"
                                    className="flex items-center gap-2 h-9 pl-1.5 pr-3 rounded-lg border border-line bg-surface shadow-card text-[13px] font-medium text-foreground hover:bg-surface-raised transition-colors duration-150"
                                >
                                    <span className="grid place-items-center w-6 h-6 rounded-md bg-accent-soft text-accent">
                                        <User size={12} />
                                    </span>
                                    {user.name?.split(" ")[0]}
                                </Link>
                                <button
                                    onClick={() => { logout(); navigate("/"); }}
                                    data-testid="navbar-logout"
                                    className="px-3 h-9 rounded-lg text-[13px] font-medium text-dim hover:text-neg hover:bg-surface-raised transition-colors duration-150"
                                >
                                    Log out
                                </button>
                            </div>
                        ) : (
                            <div className="hidden sm:flex items-center gap-2">
                                <Link
                                    to="/login"
                                    data-testid="navbar-login"
                                    className="grid place-items-center px-3 h-9 rounded-lg text-[13px] font-medium text-dim hover:text-foreground hover:bg-surface-raised transition-colors duration-150"
                                >
                                    Log in
                                </Link>
                                <Link
                                    to="/register"
                                    data-testid="navbar-register"
                                    className="grid place-items-center px-4 h-9 rounded-full bg-accent text-accent-foreground text-[13px] font-semibold shadow-card hover:brightness-95 hover:shadow-glow transition-[filter,box-shadow] duration-150"
                                >
                                    Subscribe
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Search panel */}
            {searchOpen && (
                <div className="border-t border-line bg-surface-sunken" data-testid="navbar-search-panel">
                    <form onSubmit={doSearch} className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex gap-2">
                        <div className="relative flex-1">
                            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-dim pointer-events-none" />
                            <input
                                autoFocus
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search articles, markets, authors..."
                                data-testid="navbar-search-input"
                                className="field !pl-9"
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="navbar-search-submit"
                            className="px-6 rounded-md bg-accent text-accent-foreground text-sm font-semibold shadow-card hover:brightness-95 transition-[filter] duration-150"
                        >
                            Search
                        </button>
                    </form>
                </div>
            )}

            {/* Mobile menu */}
            {mobileOpen && (
                <nav className="xl:hidden border-t border-line bg-surface" data-testid="mobile-menu">
                    <ul className="px-3 py-3">
                        {CATEGORIES.map((cat) => (
                            <li key={cat}>
                                <Link
                                    to={`/category/${cat}`}
                                    onClick={() => setMobileOpen(false)}
                                    className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium text-dim hover:text-accent hover:bg-accent-soft transition-colors duration-150"
                                >
                                    {CATEGORY_LABELS[cat]}
                                    <ArrowRight size={14} className="text-line-strong" />
                                </Link>
                            </li>
                        ))}
                        <li>
                            <Link
                                to="/archive"
                                onClick={() => setMobileOpen(false)}
                                className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium text-dim hover:text-accent hover:bg-accent-soft transition-colors duration-150"
                            >
                                Archive
                                <ArrowRight size={14} className="text-line-strong" />
                            </Link>
                        </li>
                        {!user && (
                            <li className="flex gap-2 px-3 pt-3 mt-2 border-t border-line">
                                <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1 text-center py-2.5 rounded-lg border border-line bg-surface text-sm font-semibold text-foreground">
                                    Log in
                                </Link>
                                <Link to="/register" onClick={() => setMobileOpen(false)} className="flex-1 text-center py-2.5 rounded-full bg-accent text-accent-foreground text-sm font-semibold">
                                    Subscribe
                                </Link>
                            </li>
                        )}
                        {user && (
                            <li className="px-3 pt-3 mt-2 border-t border-line space-y-2">
                                <Link to="/account" onClick={() => setMobileOpen(false)} data-testid="mobile-account" className="block w-full text-center py-2.5 rounded-lg border border-line bg-surface text-sm font-semibold text-foreground">
                                    My account
                                </Link>
                                <button onClick={() => { logout(); setMobileOpen(false); navigate("/"); }} className="w-full py-2.5 rounded-lg border border-line bg-surface text-sm font-semibold text-neg">
                                    Log out
                                </button>
                            </li>
                        )}
                    </ul>
                </nav>
            )}
        </header>
    );
}
